import { useState, useCallback } from 'react';
import type {
  FlowArrow,
  AnchorPosition,
} from '../types/whiteboard';
import { createFlowArrow } from '../types/whiteboard';

export function useArrows() {
  const [arrows, setArrows] = useState<FlowArrow[]>([]);
  const [selectedArrowId, setSelectedArrowId] = useState<string | null>(null);

  const addArrow = useCallback((
    sourceId: string,
    sourceAnchor: AnchorPosition,
    targetId: string,
    targetAnchor: AnchorPosition
  ) => {
    // Prevent self-connection
    if (sourceId === targetId) return null;

    // Prevent duplicate connections between same source and target
    const exists = arrows.some(
      a => a.sourceId === sourceId && a.targetId === targetId
    );
    if (exists) return null;

    const arrow = createFlowArrow(sourceId, sourceAnchor, targetId, targetAnchor);
    setArrows(prev => [...prev, arrow]);
    return arrow;
  }, [arrows]);

  const updateArrow = useCallback((id: string, updates: Partial<FlowArrow>) => {
    setArrows(prev => prev.map(a => a.id === id ? { ...a, ...updates } : a));
  }, []);

  const deleteArrow = useCallback((id: string) => {
    setArrows(prev => prev.filter(a => a.id !== id));
    if (selectedArrowId === id) setSelectedArrowId(null);
  }, [selectedArrowId]);

  /** Remove all arrows connected to a given element or frame */
  const removeArrowsForEntity = useCallback((entityId: string) => {
    setArrows(prev => prev.filter(
      a => a.sourceId !== entityId && a.targetId !== entityId
    ));
  }, []);

  /** Get arrows connected to an entity */
  const getConnectedArrows = useCallback((entityId: string) => {
    return arrows.filter(
      a => a.sourceId === entityId || a.targetId === entityId
    );
  }, [arrows]);

  return {
    arrows,
    setArrows,
    selectedArrowId,
    setSelectedArrowId,
    addArrow,
    updateArrow,
    deleteArrow,
    removeArrowsForEntity,
    getConnectedArrows,
  };
}
